import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PlusCircle, Search, ClipboardList, ArrowRight, Users, UserRound, MapPin, Clock } from "lucide-react";
import { api } from "../lib/api";
import { Empty, Spinner } from "../components/Bits";
import { useT } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { Photo, Logo } from "../components/Chrome";
import { AuthBox } from "./Auth";

function LatestCandidates() {
  const { t } = useT();
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/public/latest-candidates").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  return (
    <section className="page py-12" data-testid="latest-candidates">
      <h2 className="text-2xl font-bold text-[color:var(--navy)] sm:text-3xl">{t("latest_cands")}</h2>
      <p className="mt-1 text-slate-600">{t("latest_cands_sub")}</p>
      <div className="mt-6">
        {rows === null ? <Spinner /> : rows.length === 0 ? <Empty icon={Users} title={t("no_latest_cands")} testid="latest-candidates-empty" /> : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {rows.map((c, i) => (
              <div key={i} className="card-soft flex gap-4 p-4" data-testid={`anon-candidate-${i}`}>
                <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-slate-100 text-slate-400"><UserRound size={26} /></span>
                <div className="min-w-0 space-y-1 text-sm text-slate-700">
                  <p className="text-base font-bold text-slate-900">{(c.job_types || []).map((v) => t(`cat_${v}`)).join(", ") || t("cand_anon")}</p>
                  <p className="flex items-center gap-1"><MapPin size={14} />{c.location || "—"} · {c.experience ? t(`e_${c.experience}`) : "—"}</p>
                  <p className="flex items-center gap-1"><Clock size={14} />{(c.hours || []).map((h) => t(`h_${h}`)).join(", ") || "—"}</p>
                  {c.availability && <p className="font-semibold text-emerald-700">{t("can_start")} : {t(`a_${c.availability}`)}</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default function EmployerLanding() {
  const { t } = useT();
  const { user } = useAuth();
  const cta = (id) => (user?.role === "employer" ? (
    <Link to="/employer" className="btn-orange" data-testid={`${id}-dashboard-btn`}>{t("emp_start")}<ArrowRight size={22} /></Link>
  ) : (
    <div className="w-full rounded-3xl bg-white p-5 text-slate-900 shadow-sm sm:max-w-md"><AuthBox next="/employer" role="employer" testid={id} /></div>
  ));
  const feats = [[PlusCircle, "emp_f1", "emp_f1d"], [Search, "emp_f2", "emp_f2d"], [ClipboardList, "emp_f3", "emp_f3d"]];
  return (
    <div>
      <section className="page grid items-center gap-8 py-8 md:grid-cols-2 md:py-14">
        <div className="fade-up">
          <div className="mb-4 flex items-center gap-3"><Logo size={64} testid="emp-landing-logo" /><span className="rounded-full bg-[color:var(--orange-soft)] px-3 py-1 text-sm font-bold text-[color:var(--orange-dark)]">{t("for_shops")}</span></div>
          <h1 className="text-4xl font-extrabold leading-[1.05] tracking-tight text-[color:var(--navy)] sm:text-5xl">{t("emp_hero_t")}</h1>
          <p className="mt-4 text-lg text-slate-600">{t("emp_hero_sub")}</p>
          <div className="mt-8">{cta("emp-landing-auth")}</div>
        </div>
        <Photo k="welcome" eager className="h-52 w-full rounded-[2rem] sm:h-72 md:h-[440px]" />
      </section>
      <section className="bg-slate-50 py-12">
        <div className="page grid gap-4 md:grid-cols-3">
          {feats.map(([Icon, a, b]) => (
            <div key={a} className="card-soft p-6">
              <span className="grid h-14 w-14 place-items-center rounded-2xl bg-[color:var(--navy)] text-white"><Icon size={28} /></span>
              <h2 className="mt-4 text-xl font-bold text-[color:var(--navy)]">{t(a)}</h2>
              <p className="mt-1 text-slate-600">{t(b)}</p>
            </div>
          ))}
        </div>
      </section>
      <LatestCandidates />
      <section className="page mt-4">
        <div className="grid overflow-hidden rounded-[2rem] bg-[color:var(--navy)] text-white md:grid-cols-2">
          <div className="order-2 p-8 sm:p-10 md:order-1">
            <h2 className="text-2xl font-bold sm:text-3xl">{t("emp_final_t")}</h2>
            <p className="mt-2 text-white/80">{t("emp_final_sub")}</p>
            <div className="mt-6">{cta("emp-final-auth")}</div>
          </div>
          <Photo k="interview" className="order-1 h-56 w-full md:order-2 md:h-full" />
        </div>
      </section>
    </div>
  );
}
